import '@design-system/component-library/src/main.css';
import { useEffect, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { useHeadJsonLd } from '../../common/hooks/useHeadJsonLd.js';
import { useLegacyStyles } from '../../common/hooks/useLegacyStyles.js';
import { getLocale, resolveLocaleFromPath, setLocale, setSiteLocale, strToLocale } from '../../common/i18n';
import { ScrollToTop } from '../../common/utils/browserUtils.js';
import { isStrictFeatureEnabled } from '../../common/utils/featureFlagUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { DataLoaderSpinner } from '../../components/DataLoaderSpinner/DataLoaderSpinner.js';
import { Footer } from '../../components/Footer/Footer.js';
import { GenesysChat, ProactiveChatTrigger } from '../../components/GenesysChat/index.js';
import { initializeActiveAccountChangeListener, initializeShoppingCart } from '../../selfservice/init.js';
import { AuthProvider } from './AuthProvider.js';
import { ConfigProvider } from './ConfigProvider.js';
import { DialogProvider } from './DialogProvider.js';
import { NotificationProvider } from './NotificationProvider.js';
import { SessionDataProvider } from './SessionDataProvider.js';
import { SiteContext } from './SiteContext.js';
import { SubscriptionActionProvider } from './SubscriptionActionProvider.js';
import { CookieConsentWrapper } from './components/CookieConsentWrapper.js';
import { GiosgVisitorSync } from './components/GiosgVisitorSync';
import { getConfig } from './configStore.js';
import { OnlineModelContextProvider } from './onlineModel/onlineModelContextProvider';
import { SUPPORTED_SITE_LANGUAGES } from './siteUtils.js';
import type { Locale } from '../../common/i18n';
import type { State } from '../../selfservice/common/store.js';

export const SiteFn = () => {
  const store = useStore<State>();
  const layoutState = useSelector((state: State) => state.layout, deepEqual);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const featureFlags = getConfig().featureFlags;
  const genesysChatConfig = getConfig().genesysChat;

  const [siteLanguage, setSiteLanguage] = useState<Locale>(() => {
    const initialLanguage = searchParams.get('language');
    return initialLanguage ? strToLocale(initialLanguage) : getLocale();
  });

  const saveUserPreferredLocale = (locale: Locale) => {
    setLocale(locale);
    setSiteLanguage(locale);
  };

  useEffect(() => {
    const locale = resolveLocaleFromPath(pathname, siteLanguage);
    if (searchParams.get('language')) {
      setLocale(siteLanguage);
      searchParams.delete('language');
      setSearchParams(searchParams, { replace: true });
    }

    if (locale) {
      setSiteLanguage(locale.language);
      setSiteLocale(locale.content);
    }
  }, [pathname, siteLanguage, searchParams, setSearchParams]);

  type Language = 'fi' | 'en' | 'sv';
  const saveUserPreferredLanguage = (language: Language) => {
    saveUserPreferredLocale(SUPPORTED_SITE_LANGUAGES[language]);
  };

  const switchPreferredLanguage = (language: Language) => {
    if (language === 'fi') {
      navigate('/');
    } else {
      navigate(`/${language}`);
    }
    saveUserPreferredLanguage(language);
  };

  useEffect(() => {
    initializeActiveAccountChangeListener();
  }, []);

  useEffect(() => {
    initializeShoppingCart(store);
  }, [store]);

  const isOverrideOn = useLegacyStyles();
  useHeadJsonLd();

  const MainContent = (
    <OnlineModelContextProvider initialData={{}}>
      <main className={isOverrideOn ? 'override-enabled' : ''}>
        <ScrollToTop />
        <CookieConsentWrapper />
        <DataLoaderSpinner />
        <div className={`of-site${pathname === paths.ERROR ? ' of-site--error' : ''}`}>
          <Outlet key={siteLanguage} /* TODO: key needs to be removed when the i18n is cleaned up. */ />
        </div>
      </main>
      {!layoutState?.hideHeaderFooter && (
        <Footer isMaintenanceModeForCmsPages={isStrictFeatureEnabled(featureFlags.isMaintenanceModeForCmsPages)} />
      )}
      {genesysChatConfig?.deploymentId && (
        <GenesysChat deploymentId={genesysChatConfig.deploymentId} widgetCdnUrl={genesysChatConfig.widgetCdnUrl} />
      )}
      {genesysChatConfig?.deploymentId && <ProactiveChatTrigger />}
      <GiosgVisitorSync />
    </OnlineModelContextProvider>
  );

  return (
    <SiteContext.Provider
      value={{
        switchPreferredLanguage,
        saveUserPreferredLanguage,
        saveUserPreferredLocale,
        siteBaseUrl: getConfig().siteBaseUrl,
        siteLanguage,
      }}
    >
      <ConfigProvider>
        <DialogProvider>
          <NotificationProvider>
            <SubscriptionActionProvider>
              <AuthProvider>
                <SessionDataProvider>{MainContent}</SessionDataProvider>
              </AuthProvider>
            </SubscriptionActionProvider>
          </NotificationProvider>
        </DialogProvider>
      </ConfigProvider>
    </SiteContext.Provider>
  );
};
